import React from 'react';
import { ModalProps } from '@odin-labs/components';
import { ChangeType } from 'apollo/generated/client-operations';
import { Dialog } from 'dialog';
import { getTempId } from 'utils';
import { pluralize } from 'utils/pluralize';
import { SearchWorkerResult } from 'components/searchBox/types';
import {
  FormSubmissionWorkerEditSearch,
  FormSubmissionWorkerEditSearchProps,
  getJobsiteWorkerForJobsite,
} from 'containers/jobsiteFormSubmission/components/formSubmissionWorkerEditSearch';
import {
  AddedFormSubmissionWorker,
  AddFormSubmissionWorkerModalProps,
} from './types';
import { AddedWorkers } from './AddedWorkers';

// The modal actions panel has no option to hide its cancel button; Done is the
// only action while adding, since every pick is applied straight away.
const noCancelButton = (): null => null;

type SearchModeProps = Pick<
  FormSubmissionWorkerEditSearchProps,
  'onSelected' | 'onEnterSelected' | 'excludedWorkerIds' | 'afterSearchBox'
>;

export function AddFormSubmissionWorkerModal(
  props: AddFormSubmissionWorkerModalProps,
): React.ReactElement {
  const {
    isOpen,
    closeModal,
    onConfirm,
    jobsiteId,
    jobsiteFormSubmissionId,
    associationType,
    mode = 'add',
    addedWorkerIds = [],
    onRemove,
    showInitialSearchResults,
  } = props;

  // remounting the search after each add clears it and puts the focus back
  const [searchKey, setSearchKey] = React.useState(0);
  // the workers added since the modal opened
  const [sessionWorkers, setSessionWorkers] = React.useState<
    SearchWorkerResult[]
  >([]);
  React.useEffect(() => {
    if (isOpen) {
      setSessionWorkers([]);
    }
  }, [isOpen]);

  const addWorker = (searchWorkerResult: SearchWorkerResult): void => {
    const jfsWorker: AddedFormSubmissionWorker = {
      id: getTempId(),
      jobsiteWorker: getJobsiteWorkerForJobsite(searchWorkerResult, jobsiteId),
      changeType: ChangeType.Created,
      associationType,
    };
    onConfirm(jfsWorker, 'create');
  };

  const addSessionWorker = (searchWorkerResult: SearchWorkerResult): void => {
    addWorker(searchWorkerResult);
    setSessionWorkers((workers) => [...workers, searchWorkerResult]);
    setSearchKey((key) => key + 1);
  };

  const removeSessionWorker = (
    searchWorkerResult: SearchWorkerResult,
  ): void => {
    onRemove?.(searchWorkerResult.id);
    setSessionWorkers((workers) =>
      workers.filter(({ id }) => id !== searchWorkerResult.id),
    );
  };

  const {
    footerProps,
    searchProps,
  }: {
    footerProps: Partial<ModalProps>;
    searchProps: SearchModeProps;
  } =
    mode === 'addMany'
      ? {
          footerProps: {
            onAction: closeModal,
            actionText: 'Done',
            cancelButton: noCancelButton,
            additionalActionItems: (
              <span className="odin-text-sm odin-text-gray-700">
                {pluralize(sessionWorkers.length, 'worker')} added
              </span>
            ),
          },
          searchProps: {
            // workers already on the submission can't be picked again
            excludedWorkerIds: addedWorkerIds,
            onSelected: addSessionWorker,
            onEnterSelected: addSessionWorker,
            afterSearchBox: (
              <AddedWorkers
                workers={sessionWorkers}
                onRemove={removeSessionWorker}
              />
            ),
          },
        }
      : {
          footerProps: { actionsPanel: null },
          searchProps: { onSelected: addWorker },
        };

  return (
    <Dialog
      id="editFormSubmissionWorker"
      open={isOpen}
      setOpen={closeModal}
      title="Add Worker"
      {...footerProps}
    >
      <FormSubmissionWorkerEditSearch
        key={searchKey}
        jobsiteFormSubmissionId={jobsiteFormSubmissionId}
        showInitialSearchResults={showInitialSearchResults}
        {...searchProps}
      />
    </Dialog>
  );
}
